import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  products,
  canAddToCart,
  defaultVariantId,
  getProductAvailability,
  homepageContent,
  siteContent,
  toOrderProduct
} from '../../data/products';
import { useCart, formatINR } from '../../context/CartContext';
import { buildWhatsAppUrl } from '../../utils/whatsapp';
import Tilt3D from '../../motion3d/Tilt3D';
import { ParallaxImg } from '../../motion3d/Depth';
import Icon from '../Icon/Icon';
import './BestSellers.css';

const BestSellers = () => {
  const { add, qtyOf } = useCart();
  const railRef = useRef(null);
  // Edge fades hint that the phone-width rail keeps going. Above 560px the
  // grid is a real grid, nothing overflows, and both flags stay false.
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const configuredIds = homepageContent.bestSellers.productIds;
  const picks = (configuredIds.length
    ? configuredIds
    : products.filter((product) => product.active !== false && product.bestSeller).map((product) => product.id))
    .map((id) => products.find((p) => p.id === id))
    .filter((product) => product && product.active !== false);

  if (!picks.length) return null;

  return (
    <section id="best-sellers" className="best-sellers" aria-labelledby="best-sellers-title">
      <div className="best-sellers-inner">
        <header className="best-sellers-header">
          <span className="section-tag">{homepageContent.bestSellers.eyebrow}</span>
          <h2 id="best-sellers-title" className="section-title">{homepageContent.bestSellers.title}</h2>
          <p className="section-subtitle">{homepageContent.bestSellers.subtitle}</p>
        </header>

        <div
          className={`bs-rail${canScrollLeft ? ' can-left' : ''}${canScrollRight ? ' can-right' : ''}`}
        >
          <span className="bs-fade bs-fade-left" aria-hidden="true" />
          <span className="bs-fade bs-fade-right" aria-hidden="true" />
          <div className="best-sellers-grid" ref={railRef}>
            {picks.map((product) => {
              const cartProduct = toOrderProduct(product, defaultVariantId(product));
              const qty = qtyOf(product.id, cartProduct.variantId);
              const canAdd = canAddToCart(cartProduct);
              const soldOut = product.availability === 'sold-out';
              const availability = getProductAvailability(product);
              const priceLabel = cartProduct.priceLabel || formatINR(cartProduct.price);
              const waHref = buildWhatsAppUrl(siteContent.contact.whatsappNumber,
                soldOut
                  ? `Hi ${siteContent.brand.name}! Please let me know when ${product.name} is available again.`
                  : `Hi ${siteContent.brand.name}! I'd like to order: ${cartProduct.name} (${priceLabel})`
              );
              return (
                <Tilt3D className={`bs-card availability-${availability.tone}`} rotationFactor={5} key={product.id}>
                  <div className="bs-img-wrap">
                    <ParallaxImg
                      className="bs-img"
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      strength={12}
                    />
                    <span className={`bs-status status-${availability.tone}`}>
                      <i aria-hidden="true" />
                      {availability.label}
                    </span>
                  </div>
                  <div className="bs-body">
                    <div className="bs-top-row">
                      <h3 className="bs-name">{product.name}</h3>
                      <span className="bs-price">{priceLabel}</span>
                    </div>
                    <p className="bs-desc">{product.desc}</p>
                    <div className="bs-lead-time">
                      <Icon name="clock" size={14} strokeWidth={1.7} />
                      <span>{product.leadTime || siteContent.ordering.defaultLeadTime}</span>
                    </div>
                    <div className="bs-actions">
                      <button
                        type="button"
                        className={`bs-add${qty > 0 ? ' in-cart' : ''}${!canAdd ? ' is-unavailable' : ''}`}
                        onClick={() => add(product.id, cartProduct.variantId)}
                        disabled={!canAdd || qty >= siteContent.ordering.maxQuantity}
                        aria-label={
                          soldOut
                            ? `${product.name} is sold out`
                            : qty > 0
                            ? `Add another ${product.name} to cart, ${qty} in cart`
                            : `Add ${product.name} to cart`
                        }
                      >
                        {soldOut ? 'Sold out' : qty >= siteContent.ordering.maxQuantity ? `Max · ${qty}` : qty > 0 ? `In cart · ${qty}` : 'Add'}
                      </button>
                      <a
                        className="bs-order-link"
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${soldOut ? 'Notify me about' : 'Order Now:'} ${product.name} on WhatsApp`}
                      >
                        {soldOut ? 'Notify me' : 'Order Now'}
                      </a>
                    </div>
                  </div>
                </Tilt3D>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestSellers;
